import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useState } from 'react';

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const { data: shopData, isLoading } = useQuery({
    queryKey: ['shop'],
    queryFn: async () => {
      const response = await api.get('/shop');
      return response.data.data;
    },
  });

  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => {
      const response = await api.get('/users/me/wallet');
      return response.data.data.wallet;
    },
  });

  const categories = [
    { id: 'all', name: 'All Items' },
    { id: 'card_back', name: 'Card Backs' },
    { id: 'table_theme', name: 'Table Themes' },
    { id: 'avatar', name: 'Avatars' },
    { id: 'emote_pack', name: 'Emotes' },
    { id: 'profile_frame', name: 'Frames' },
  ];

  const filteredItems =
    selectedCategory === 'all'
      ? shopData?.items
      : shopData?.items?.filter((item: { item_type: string }) => item.item_type === selectedCategory);

  const handlePurchase = async (itemId: string, currency: 'coins' | 'gems') => {
    try {
      await api.post('/shop/purchase', { itemId, currency });
      // Refetch wallet
    } catch (error) {
      console.error('Purchase failed:', error);
    }
  };

  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">Shop</h1>
          {walletData && (
            <div className="flex space-x-4">
              <div className="bg-yellow-600/20 text-yellow-400 px-4 py-2 rounded-lg flex items-center space-x-2">
                <span>Coins:</span>
                <span className="font-bold">{walletData.coins}</span>
              </div>
              <div className="bg-purple-600/20 text-purple-400 px-4 py-2 rounded-lg flex items-center space-x-2">
                <span>Gems:</span>
                <span className="font-bold">{walletData.gems}</span>
              </div>
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg transition ${
                selectedCategory === cat.id
                  ? 'bg-shark-500 text-white'
                  : 'bg-shark-800 text-gray-400 hover:text-white'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Items Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shark-400 mx-auto"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems?.map(
              (item: {
                id: string;
                name: string;
                description: string;
                rarity: string;
                price_coins?: number;
                price_gems?: number;
                item_type: string;
              }) => (
                <div
                  key={item.id}
                  className={`bg-shark-800 rounded-xl overflow-hidden border-2 ${
                    item.rarity === 'legendary'
                      ? 'border-yellow-500'
                      : item.rarity === 'epic'
                      ? 'border-purple-500'
                      : item.rarity === 'rare'
                      ? 'border-blue-500'
                      : 'border-shark-700'
                  }`}
                >
                  <div className="h-40 bg-shark-700 flex items-center justify-center">
                    <span className="text-4xl">
                      {item.item_type === 'avatar' ? '👤' : item.item_type === 'card_back' ? '🃏' : '✨'}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{item.name}</h3>
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          item.rarity === 'legendary'
                            ? 'bg-yellow-600'
                            : item.rarity === 'epic'
                            ? 'bg-purple-600'
                            : item.rarity === 'rare'
                            ? 'bg-blue-600'
                            : 'bg-gray-600'
                        }`}
                      >
                        {item.rarity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">{item.description}</p>
                    <div className="flex justify-between items-center">
                      {item.price_coins && (
                        <button
                          onClick={() => handlePurchase(item.id, 'coins')}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {item.price_coins} Coins
                        </button>
                      )}
                      {item.price_gems && (
                        <button
                          onClick={() => handlePurchase(item.id, 'gems')}
                          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {item.price_gems} Gems
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
